// Grant 2026-11-02 ("when there are more courts than fit and the grid scrolls horizontally, it isn't obvious there are
// more columns off-screen"; the date strip "shows its white edge gradient even when scrolled fully to the start or the
// end, implying more to scroll when there isn't"): ONE source for a sideways scroller's edge cues. `cues` says whether
// there is more to scroll left and right (scrollCues: scrollLeft against scrollWidth - clientWidth, a 1px tolerance),
// kept fresh on the first render, on every scroll and on a resize (ResizeObserver); `viewW` is the scroller's visible
// width. The booking grid's columns (fade + arrow per edge) and the week strip's days (fade per edge) both read it, so
// the two behave alike; a fade or arrow shows only while there is more that way, nothing when everything fits.
import {onBeforeUnmount, ref, watch} from 'vue';
import {scrollCues} from '~/utils/bookingDrag';

/**
 * @param el a ref to the element that scrolls sideways (null until mounted, or while it isn't on screen)
 * @param onResize called after a resize has been measured (the strip scrolls the chosen day back into view)
 */
export function useScrollCues(el, {onResize = null} = {}) {
  const cues = ref({left: false, right: false});
  const viewW = ref(0);
  let ro = null;
  const update = () => {
    const s = el.value;
    const next = s ? scrollCues(s) : {left: false, right: false};
    if (next.left !== cues.value.left || next.right !== cues.value.right) cues.value = next;
    viewW.value = s ? s.clientWidth : 0;
  };
  const resized = () => { update(); onResize?.(); };
  watch(el, (now, old) => {
    old?.removeEventListener('scroll', update);
    ro?.disconnect();
    ro = null;
    if (now) {
      now.addEventListener('scroll', update, {passive: true});
      if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(resized); ro.observe(now); }
    }
    update();
  }, {immediate: true});
  onBeforeUnmount(() => { el.value?.removeEventListener('scroll', update); ro?.disconnect(); ro = null; });
  return {cues, viewW, update};
}
